// Pestaña Resumen: producto financiado, condiciones del crédito y estado a hoy, con el acomodo
// exacto del mock. Los agregados del estado los publica el silo; el próximo cupón sale del plan.
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { SaleFinancierEnum } from '@fiado/type-kit/bin/retailWizard/enums/SaleFinancierEnum';
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import type { LoanCreditDetail, LoanDeviceFleetItem } from '@/api/services/loan-credit';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import type { CreditFileBlock } from '../api/useCreditFile';
import {
    clientLevelLabelOf,
    creditAlertOf,
    formatMoneyCents,
    formatTnaPct,
    lockScheduleOf,
    NO_VALUE,
} from '../logic';
import { lockReadingOf } from '../../lockPhase';
import { overrideNoteOf, termOverridesOf, type TermOverride } from '../rationale';
import { CreditFinancedCard } from './CreditFinancedCard';
import { CreditRationaleCard } from './CreditRationaleCard';
import { CreditAlertBox, CreditCard, CreditChip, CreditKeyValue } from './CreditFileUi';
import { wireLookup } from '@/lib/wireLookup';
import { saleProductNameOf } from '../../saleContext';

/**
 * El detalle del crédito con sus agregados OPCIONALES: el silo todavía no los publica y un ausente
 * no es un cero. Cada uno se pinta con su centinela hasta que el endpoint aterrice.
 */
export type CreditSummaryCredit = LoanCreditResponse & Partial<LoanCreditDetail>;

/** Rótulo de quién fondea el crédito. Este silo solo emite `SUREKEEP`. */
const FINANCIER_LABEL: Record<SaleFinancierEnum, string> = {
    [SaleFinancierEnum.NONE]: 'Sin financiera',
    [SaleFinancierEnum.SUREKEEP]: 'SureKeep',
    [SaleFinancierEnum.PAYJOY]: 'PayJoy',
    [SaleFinancierEnum.CREDIYA]: 'Crediya',
    [SaleFinancierEnum.LESPAGO]: 'Lespago',
    [SaleFinancierEnum.INNOVA]: 'Innova',
};

/** El rótulo de la financiera; un valor que el front no conoce se pinta tal cual. */
export function financierLabelOf(financier: string | undefined): string | undefined {
    return financier ? (wireLookup(FINANCIER_LABEL, financier) ?? financier) : undefined;
}

/** Marca y modelo de la venta, igual que el hero de Cobranza; sin ellos, el SKU. Nunca los del MDM. */
export function productNameOf(credit: CreditSummaryCredit): string {
    const name = credit.saleContext ? saleProductNameOf(credit.saleContext) : undefined;
    return name ?? credit.productSku ?? NO_VALUE;
}

/** «Estado a hoy»: solo la alerta del crédito; saldo, cuotas y mora ya van en el encabezado. */
function TodayStatus({
    credit,
    arrearsDays,
    device,
}: {
    credit: CreditSummaryCredit;
    arrearsDays: number | null;
    device: LoanDeviceFleetItem | null | undefined;
}) {
    // `undefined` es que el expediente MDM no se pudo leer, que no es «el equipo está libre».
    const lock = device ? lockScheduleOf(device) : { at: undefined, scheduled: false };
    const deviceLocked =
        device === undefined ? null : device?.mdmStatus === LoanDeviceMdmStatusEnum.LOCKED;

    return (
        <CreditAlertBox
            alert={creditAlertOf({
                status: credit.status,
                overdueDays: arrearsDays,
                deviceLocked,
                lockedAt: lock.at,
                lockReading: device ? lockReadingOf(device) : null,
                lockReason: device?.lockReason,
                lockMode: device?.mdmLockMode,
            })}
        />
    );
}

/**
 * Marca de que ese valor NO es el del plan pelado. El chip dice en corto quién lo movió y la frase
 * entera se lee al pasar el cursor y con lector de pantalla.
 */
export function CreditTermMark({ override }: { override: TermOverride }) {
    const note = overrideNoteOf(override);
    if (note === undefined) return null;
    const byPromotion = override.promotions.length > 0;
    const byPolicy = override.policies.length > 0;
    return (
        <span className="cursor-help" title={note}>
            <CreditChip tone={byPolicy ? 'warning' : 'info'}>
                {byPromotion && byPolicy ? 'promo · política' : byPromotion ? 'promo' : 'política'}
            </CreditChip>
            <span className="sr-only">{note}</span>
        </span>
    );
}

/** «Nivel · financiera» en un renglón; lo que falte se omite. */
function levelAndFinancierOf(credit: CreditSummaryCredit): string | undefined {
    const level = credit.clientLevelAtOrigination;
    const parts = [
        level ? clientLevelLabelOf(level) : undefined,
        financierLabelOf(credit.financier),
    ].filter((part): part is string => part !== undefined);
    return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** Condiciones del crédito. `compact` junta cuota con plazo y nivel con financiera (Cobranza). */
export function CreditTermsCard({
    credit,
    compact = false,
}: {
    credit: CreditSummaryCredit;
    compact?: boolean;
}) {
    const level = credit.clientLevelAtOrigination;
    const overrides = termOverridesOf(credit);
    const weekly = formatMoneyCents(credit.weeklyPaymentWithIvaCents);

    return (
        <CreditCard title="Condiciones del crédito">
            {compact ? null : (
                <CreditKeyValue
                    label="Nivel del cliente"
                    value={level ? `Nivel ${clientLevelLabelOf(level)}` : undefined}
                />
            )}
            <CreditKeyValue
                label="Enganche"
                value={formatMoneyCents(credit.downPaymentCents)}
                mark={<CreditTermMark override={overrides.downPayment} />}
            />
            <CreditKeyValue
                label="Principal"
                value={formatMoneyCents(credit.financedCapitalCents)}
                mark={<CreditTermMark override={overrides.financedCapital} />}
            />
            <CreditKeyValue
                label="Tasa anual"
                value={formatTnaPct(credit.tnaAnnual)}
                mark={<CreditTermMark override={overrides.tnaAnnual} />}
            />
            {compact ? (
                <>
                    <CreditKeyValue
                        label="Cuota semanal"
                        value={`${weekly} × ${String(credit.termWeeks)}`}
                    />
                    <CreditKeyValue
                        label="Nivel · financiera"
                        value={levelAndFinancierOf(credit)}
                    />
                </>
            ) : (
                <>
                    <CreditKeyValue label="Cuota sem." value={weekly} />
                    <CreditKeyValue label="Plazo" value={`${String(credit.termWeeks)} semanas`} />
                    <CreditKeyValue label="Financiera" value={financierLabelOf(credit.financier)} />
                </>
            )}
        </CreditCard>
    );
}

export interface CreditSummaryTabProps {
    credit: CreditSummaryCredit;
    /** El expediente MDM del equipo: de ahí salen la fecha y el motivo del bloqueo. */
    device: CreditFileBlock<LoanDeviceFleetItem | null>;
    /** La mora ya resuelta con `resolveArrearsDays`, la misma que pinta el encabezado. */
    arrearsDays: number | null;
    /** Líneas de la venta que originó el crédito; sin ellas la card cae al equipo y el total. */
    saleLines?: CreditFileBlock<SaleLine[] | null>;
}

export function CreditSummaryTab({
    credit,
    device,
    arrearsDays,
    saleLines,
}: CreditSummaryTabProps) {
    return (
        <>
            <CreditFinancedCard
                totalCents={credit.equipmentPriceCents}
                productName={productNameOf(credit)}
                {...(saleLines !== undefined && { saleLines })}
            />

            <CreditTermsCard credit={credit} />

            <CreditRationaleCard credit={credit} />

            <CreditTodayCard credit={credit} device={device} arrearsDays={arrearsDays} />
        </>
    );
}

/** El bloque «Estado a hoy»: la alerta del crédito. */
export function CreditTodayCard({
    credit,
    device,
    arrearsDays,
}: Pick<CreditSummaryTabProps, 'credit' | 'device' | 'arrearsDays'>) {
    return (
        <CreditCard title="Estado a hoy">
            <TodayStatus
                credit={credit}
                arrearsDays={arrearsDays}
                device={device.failure !== null || device.isLoading ? undefined : device.data}
            />
        </CreditCard>
    );
}
